import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import type { RootState } from "@/store"
import type { OffboardingDto, OffboardingDocumentDto } from "@/modules/offboarding/dtos"
import { offboardingService } from "@/modules/offboarding/infrastructure/offboarding-service"
import { selectCurrentOrganizationId } from "@/modules/organizations/store/organizationSlice"

interface OffboardingState {
  items: OffboardingDto[]
  selectedItem: OffboardingDto | null
  isLoading: boolean
  isLoadingDetail: boolean
  error: string | null
}

const initialState: OffboardingState = {
  items: [],
  selectedItem: null,
  isLoading: false,
  isLoadingDetail: false,
  error: null,
}

export const fetchOffboardingsThunk = createAsyncThunk<
  OffboardingDto[],
  { searchTerm?: string; resignationStatus?: string } | void,
  { state: RootState; rejectValue: string }
>("offboarding/fetchAll", async (arg, { getState, rejectWithValue }) => {
  try {
    const orgId = selectCurrentOrganizationId(getState())
    if (!orgId) throw new Error("No organisation selected")
    const searchTerm = arg && typeof arg === "object" ? arg.searchTerm : undefined
    const resignationStatus = arg && typeof arg === "object" ? arg.resignationStatus : undefined
    return await offboardingService.getOffboardings(orgId, searchTerm, resignationStatus)
  } catch (err: unknown) {
    return rejectWithValue(err instanceof Error ? err.message : "Failed to load offboardings")
  }
})

export const fetchOffboardingByIdThunk = createAsyncThunk<
  OffboardingDto,
  string,
  { state: RootState; rejectValue: string }
>("offboarding/fetchById", async (id, { getState, rejectWithValue }) => {
  try {
    const orgId = selectCurrentOrganizationId(getState())
    if (!orgId) throw new Error("No organisation selected")
    return await offboardingService.getOffboardingById(orgId, id)
  } catch (err: unknown) {
    return rejectWithValue(err instanceof Error ? err.message : "Failed to load offboarding")
  }
})

const offboardingSlice = createSlice({
  name: "offboarding",
  initialState,
  reducers: {
    updateDocumentInSelected(state, action: PayloadAction<OffboardingDocumentDto>) {
      if (!state.selectedItem) return
      state.selectedItem.documents = state.selectedItem.documents.map((d) =>
        d.id === action.payload.id ? action.payload : d,
      )
    },
    clearSelectedOffboarding(state) {
      state.selectedItem = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOffboardingsThunk.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchOffboardingsThunk.fulfilled, (state, action) => {
        state.isLoading = false
        state.items = action.payload
      })
      .addCase(fetchOffboardingsThunk.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload ?? "Failed to load offboardings"
      })
      .addCase(fetchOffboardingByIdThunk.pending, (state) => {
        state.isLoadingDetail = true
        state.error = null
      })
      .addCase(fetchOffboardingByIdThunk.fulfilled, (state, action) => {
        state.isLoadingDetail = false
        state.selectedItem = action.payload
      })
      .addCase(fetchOffboardingByIdThunk.rejected, (state, action) => {
        state.isLoadingDetail = false
        state.error = action.payload ?? "Failed to load offboarding"
      })
  },
})

export const { updateDocumentInSelected, clearSelectedOffboarding } = offboardingSlice.actions
export default offboardingSlice.reducer

export const selectOffboardings = (state: RootState) => state.offboarding?.items ?? []
export const selectSelectedOffboarding = (state: RootState) => state.offboarding?.selectedItem ?? null
export const selectOffboardingLoading = (state: RootState) => state.offboarding?.isLoading ?? false
export const selectOffboardingDetailLoading = (state: RootState) => state.offboarding?.isLoadingDetail ?? false
export const selectOffboardingError = (state: RootState) => state.offboarding?.error ?? null
